import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState, type KeyboardEvent } from 'react';
import { ApiError, type AdminApi, type TagRef } from '../../api/client.ts';

interface Props {
  environmentId: string;
  objectType: string;
  objectId: string;
  /** The employee may edit the object (its edit permission); otherwise the tags are shown read-only. */
  canEdit: boolean;
}

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/** Tags of one object (BB-18): add existing tags, create a new one inline, remove. */
export function TagPicker({ environmentId, objectType, objectId, canEdit }: Props) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const assignedKey = ['object-tags', environmentId, objectType, objectId];
  const all = useQuery({ queryKey: ['tags', environmentId], queryFn: () => api.listTags(environmentId) });
  const assigned = useQuery({ queryKey: assignedKey, queryFn: () => api.objectTags(environmentId, objectType, objectId) });
  const [text, setText] = useState('');
  const current = assigned.data ?? [];
  const save = useMutation({
    mutationFn: (tagIds: string[]) => api.setObjectTags(environmentId, objectType, objectId, tagIds),
    onSuccess: (tags) => {
      queryClient.setQueryData(assignedKey, tags);
      void queryClient.invalidateQueries({ queryKey: ['tags', environmentId] });
      setText('');
    },
  });
  const create = useMutation({
    mutationFn: (name: string) => api.createTag(environmentId, { name }),
    onSuccess: (tag) => save.mutate([...current.map((t) => t.id), tag.id]),
  });

  const name = text.trim();
  const free = (all.data ?? []).filter((t) => !current.some((c) => c.id === t.id));
  const matches = name ? free.filter((t) => t.name.toLowerCase().includes(name.toLowerCase())).slice(0, 8) : [];
  const exact = (all.data ?? []).find((t) => t.name.toLowerCase() === name.toLowerCase());
  const add = (tag: TagRef) => save.mutate([...current.map((t) => t.id), tag.id]);
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || !name) return;
    event.preventDefault();
    if (exact) {
      if (!current.some((c) => c.id === exact.id)) add(exact);
    } else create.mutate(name);
  };
  const busy = save.isPending || create.isPending;
  const error = save.error ?? create.error;

  return (
    <div role="group" aria-label="Tags" className="tag-picker">
      {current.length === 0 && !canEdit ? <span className="hint">No tags</span> : null}
      {current.map((tag) => (
        <span key={tag.id} className="tag tag--neutral">
          {tag.name}
          {canEdit && (
            <button type="button" className="tag__remove" aria-label={`Remove tag ${tag.name}`} disabled={busy} onClick={() => save.mutate(current.filter((t) => t.id !== tag.id).map((t) => t.id))}>
              ×
            </button>
          )}
        </span>
      ))}
      {canEdit && (
        <>
          <input aria-label="Add tag" placeholder="Add tag…" value={text} disabled={busy} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} />
          {name && (
            <ul aria-label="Tag suggestions" className="tag-picker__suggestions">
              {matches.map((tag) => (
                <li key={tag.id}>
                  <button type="button" onClick={() => add(tag)} disabled={busy}>
                    {tag.name}
                  </button>
                </li>
              ))}
              {!exact && (
                <li>
                  <button type="button" onClick={() => create.mutate(name)} disabled={busy}>
                    Create tag “{name}”
                  </button>
                </li>
              )}
            </ul>
          )}
          {error && <span role="alert">{errorText(error)}</span>}
        </>
      )}
    </div>
  );
}
